import { activeCellChanged, beginAction, getCell, getCellAddress, getCellIndexes, getSortedIndex, getRowHeight, getSheetName, importModelUpdate, setCell } from '../../workbook/index';
import { initiateComment, completeAction, createCommentIndicator, deleteComment, removeCommentContainer, locale, replyToComment, showCommentsPane, refreshCommentsPane, commentUndoRedo, getDPRValue, processSheetComments } from '../index';
import { Browser, closest, detach, enableRipple, EventHandler, getComponent, getUniqueID, isNullOrUndefined } from '@syncfusion/ej2-base';
import { getUpdateUsingRaf, navigateNextPrevComment, updateNoteContainer, destroyComponent } from './../common/index';
import { getRangeAddress, updateCell, getISOTime } from '../../workbook/index';
import { Button } from '@syncfusion/ej2-buttons';
import { DropDownButton } from '@syncfusion/ej2-splitbuttons';
import { ListView, Virtualization } from '@syncfusion/ej2-lists';
ListView.Inject(Virtualization);
/**
 * Comment module.
 */
export class SpreadsheetComment {
    /**
     * Initializes a new instance of the `SpreadsheetComment` class
     *
     * @param {Spreadsheet} parent - Constructor for SpreadsheetComment module.
     */
    constructor(parent) {
        /** @hidden */
        this.isCommentVisible = false;
        /** @hidden */
        this.isCommentVisibleOnTouch = false;
        /** @hidden */
        this.isReviewPaneVisible = false;
        this.isEditing = false;
        this.reviewInstances = [];
        this.reviewFilter = 'all';
        this.scheduleMountId = 0;
        this.parent = parent;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.on(initiateComment, this.initiateComment, this);
        this.parent.on(deleteComment, this.deleteComment, this);
        this.parent.on(createCommentIndicator, this.createCommentIndicator, this);
        this.parent.on(removeCommentContainer, this.removeCommentContainer, this);
        this.parent.on(replyToComment, this.replyToComment, this);
        this.parent.on(importModelUpdate, this.updateCommentsFromSheet, this);
        this.parent.on(showCommentsPane, this.showCommentPane, this);
        this.parent.on(navigateNextPrevComment, this.navigateNextPrevComment, this);
        this.parent.on(refreshCommentsPane, this.refreshCommentsPane, this);
        this.parent.on(processSheetComments, this.processSheetComments, this);
        this.parent.on(commentUndoRedo, this.onCommentUndoRedo, this);
        this.parent.on(activeCellChanged, this.commentHandler, this);
    }
    removeEventListener() {
        if (!this.parent.isDestroyed) {
            this.parent.off(initiateComment, this.initiateComment);
            this.parent.off(deleteComment, this.deleteComment);
            this.parent.off(createCommentIndicator, this.createCommentIndicator);
            this.parent.off(removeCommentContainer, this.removeCommentContainer);
            this.parent.off(replyToComment, this.replyToComment);
            this.parent.off(importModelUpdate, this.updateCommentsFromSheet);
            this.parent.off(showCommentsPane, this.showCommentPane);
            this.parent.off(navigateNextPrevComment, this.navigateNextPrevComment);
            this.parent.off(refreshCommentsPane, this.refreshCommentsPane);
            this.parent.off(processSheetComments, this.processSheetComments);
            this.parent.off(commentUndoRedo, this.onCommentUndoRedo);
            this.parent.off(activeCellChanged, this.commentHandler);
        }
    }
    initiateComment(args) {
        const cellIndexes = (args && !isNullOrUndefined(args.rowIndex) && !isNullOrUndefined(args.columnIndex)) ?
            [args.rowIndex, args.columnIndex] : getCellIndexes(this.parent.getActiveSheet().activeCell);
        const targetElement = this.parent.getCell(cellIndexes[0], cellIndexes[1]);
        if (!isNullOrUndefined(targetElement)) {
            if (!targetElement.querySelector('.e-comment-indicator')) {
                this.createCommentIndicator({ targetEle: targetElement, rIdx: cellIndexes[0], cIdx: cellIndexes[1] });
            }
            if (args && args.isMouseOver) {
                this.createCommentContainer(targetElement, cellIndexes[0], cellIndexes[1]);
                this.activeCommentCell = [cellIndexes[0], cellIndexes[1]];
                return;
            }
            if (this.isReviewPaneVisible) {
                const sheet = this.parent.getActiveSheet();
                const existing = (sheet.comments || []).find((t) => t.address && t.address[0] === cellIndexes[0] && t.address[1] === cellIndexes[1]);
                if (existing && existing.id) {
                    this.scrollToThreadInPanel(existing.id, args && args.isSelection);
                }
                else {
                    this.renderNewCommentForPanel(cellIndexes[0], cellIndexes[1]);
                }
            }
            else {
                this.createCommentContainer(targetElement, cellIndexes[0], cellIndexes[1]);
                this.activeCommentCell = [cellIndexes[0], cellIndexes[1]];
            }
        }
    }
    createCommentIndicator(args) {
        const commentIndicator = this.parent.createElement('div', { className: 'e-comment-indicator' });
        if (args.targetEle.children.length > 0) {
            const rowHeight = getRowHeight(this.parent.getActiveSheet(), args.rIdx);
            const defaultFilterButtonHeight = 20;
            for (let i = 0; i < args.targetEle.childElementCount; i++) {
                const children = args.targetEle.children[i];
                if (children.className.indexOf('e-filter-btn') > -1) {
                    if (this.parent.enableRtl) {
                        commentIndicator.style.left = (rowHeight < (defaultFilterButtonHeight + 10) ?
                            (children.getBoundingClientRect().width <= 0 ? defaultFilterButtonHeight :
                                children.getBoundingClientRect().width + 1) : 2) + 'px';
                    }
                    else {
                        commentIndicator.style.right = (rowHeight < (defaultFilterButtonHeight + 10) ?
                            (children.getBoundingClientRect().width <= 0 ? defaultFilterButtonHeight :
                                children.getBoundingClientRect().width + 1) : 2) + 'px';
                    }
                }
                if (children.className.indexOf('e-validation-list') > -1) {
                    if (this.parent.enableRtl) {
                        commentIndicator.style.left = `${(children.getBoundingClientRect().width || 20) + 2}px`;
                    }
                    else {
                        commentIndicator.style.right = `${(children.getBoundingClientRect().width || 20) + 2}px`;
                    }
                }
            }
        }
        if (!commentIndicator.dataset.commentListenersAdded && !args.skipEvent) {
            commentIndicator.dataset.commentRowIndex = args.rIdx.toString();
            commentIndicator.dataset.commentColIndex = args.cIdx.toString();
            EventHandler.add(commentIndicator, 'mouseover', this.mouseOver, this);
            EventHandler.add(commentIndicator, 'mouseout', this.mouseOut, this);
            commentIndicator.dataset.commentListenersAdded = 'true';
        }
        args.targetEle.appendChild(commentIndicator);
    }
    mouseOver(event) {
        const cell = event.currentTarget;
        const row = parseInt(cell.dataset.commentRowIndex, 10);
        const col = parseInt(cell.dataset.commentColIndex, 10);
        if (!isNaN(row) && !isNaN(col)) {
            const containerInDOM = document.getElementsByClassName('e-comment-container')[0];
            if ((this.isCommentVisibleOnTouch && !isNullOrUndefined(containerInDOM)) || isNullOrUndefined(containerInDOM)) {
                if (!isNullOrUndefined(containerInDOM)) {
                    this.removeCommentContainer();
                }
                this.initiateComment({ rowIndex: row, columnIndex: col, isMouseOver: true });
                this.isCommentVisible = true;
            }
        }
    }
    mouseOut(e) {
        const commentContainer = this.getCommentContainer();
        const relatedTarget = e.relatedTarget;
        if (this.isCommentVisible && (!this.isCommentVisibleOnTouch && commentContainer)) {
            if (relatedTarget) {
                const isInsideContainer = commentContainer.contains(relatedTarget);
                const isInsideIndicator = closest(relatedTarget, '.e-comment-indicator');
                const isInsideDropDownPopup = closest(relatedTarget, '.e-dropdown-popup');
                const isCommentCell = this.parent.getCell(this.activeCommentCell[0], this.activeCommentCell[1]);
                if (!isInsideContainer && !isInsideIndicator && !isInsideDropDownPopup &&
                    isCommentCell && !isCommentCell.contains(relatedTarget) &&
                    document.activeElement !== commentContainer &&
                    document.activeElement !== commentContainer.querySelector('.e-comment-input')) {
                    this.removeCommentContainer();
                    this.isCommentVisible = false;
                    this.activeCommentCell = null;
                }
            }
        }
    }
    createCommentContainer(targetEle, rIdx, cIdx) {
        const commentContainer = this.parent.createElement('div', { className: 'e-comment-container' });
        commentContainer.tabIndex = -1;
        this.renderCommentUI(commentContainer, rIdx, cIdx, false);
        commentContainer.style.visibility = 'hidden';
        this.parent.element.appendChild(commentContainer);
        this.setCommentContainerPosition(commentContainer, targetEle.getBoundingClientRect());
        commentContainer.style.visibility = '';
        const cell = getCell(rIdx, cIdx, this.parent.getActiveSheet());
        const thread = cell && cell.comment;
        if (thread && thread.isResolved) {
            this.handleResolvedThread(commentContainer, rIdx, cIdx, thread);
        }
        const textArea = commentContainer.querySelector('.e-comment-footer .e-comment-input');
        if (textArea && (!cell || (cell && !cell.comment))) {
            textArea.focus();
            textArea.select();
            if (!commentContainer.classList.contains('active')) {
                commentContainer.classList.add('active');
            }
        }
        this.bindContainerEvents(commentContainer);
        EventHandler.add(commentContainer, 'mouseout', this.mouseOut, this);
        EventHandler.add(targetEle, 'mouseout', this.mouseOut, this);
        this.isCommentVisible = true;
    }
    handleResolvedThread(container, rIdx, cIdx, thread) {
        const headerEl = container.querySelector('.e-comment-header');
        if (headerEl) {
            headerEl.replaceWith(this.createheaderContent(false, getCellAddress(rIdx, cIdx), thread));
        }
        const footerEl = container.querySelector('.e-comment-footer');
        if (footerEl) {
            this.unwireFooterEvents(footerEl);
            this.removeFooterButtons(footerEl);
            footerEl.remove();
        }
        container.classList.add('e-thread-resolved');
        this.removeReplyButtons(container.querySelector('.e-comment-body'));
        this.setTextAreaState(container, true);
        this.renderResolvedWrap(container, rIdx, cIdx, thread.author || (this.parent.author || 'Guest User'));
    }
    renderCommentUI(container, rIdx, cIdx, inPane, containerId) {
        const sheet = this.parent.getActiveSheet();
        const cell = getCell(rIdx, cIdx, sheet);
        let header;
        let body;
        let footer;
        if (cell && cell.comment) {
            const cellAddress = getCellAddress(rIdx, cIdx);
            header = this.createheaderContent(false, cellAddress, cell.comment);
            body = this.createBodyContent(cell.comment, true, inPane, containerId);
            footer = this.createFooterContent(rIdx, cIdx, false);
        }
        else {
            header = this.createheaderContent(true);
            footer = this.createFooterContent(rIdx, cIdx, true);
        }
        if (!inPane) {
            container.dataset.commentRowIndex = rIdx.toString();
            container.dataset.commentColIndex = cIdx.toString();
        }
        container.appendChild(header);
        if (body) {
            container.appendChild(body);
        }
        container.appendChild(footer);
    }
    createheaderContent(initial, cellAddress, comment) {
        const header = this.parent.createElement('div', { className: 'e-comment-header' });
        const headerwrap = this.parent.createElement('div', { className: 'e-comment-header-wrap' });
        const titleWrap = this.parent.createElement('div', { className: 'e-comment-title-wrap' });
        const thread = comment;
        if (!initial && thread && thread.isResolved) {
            const l10n = this.parent.serviceLocator.getService(locale);
            const resolvedWrap = this.parent.createElement('div', { className: 'e-comment-resolved' });
            const tick = this.parent.createElement('span', { className: 'e-icons e-check' });
            const label = this.parent.createElement('div', { className: 'e-resolve-text' });
            label.textContent = l10n.getConstant('Resolved');
            resolvedWrap.appendChild(tick);
            resolvedWrap.appendChild(label);
            headerwrap.appendChild(resolvedWrap);
            this.renderHeaderActions(headerwrap, false, '', thread);
            header.appendChild(headerwrap);
            return header;
        }
        const authorName = initial ? (this.parent.author || 'Guest User') : (thread && thread.author);
        const avatar = this.getAvatar(authorName);
        const title = this.parent.createElement('div', { className: 'e-comment-title' });
        title.textContent = authorName;
        titleWrap.appendChild(title);
        headerwrap.appendChild(titleWrap);
        this.renderHeaderActions(headerwrap, initial, cellAddress, thread);
        header.appendChild(avatar);
        header.appendChild(headerwrap);
        return header;
    }
    getAvatar(author) {
        const avatar = this.parent.createElement('div', { className: 'e-comment-avatar' });
        avatar.textContent = this.getAvatarInitials(author);
        const currentUser = this.parent.author || 'Guest User';
        if (author === currentUser) {
            avatar.style.backgroundColor = '#b5082e';
        }
        else {
            avatar.style.backgroundColor = this.getAuthorColor(author);
        }
        return avatar;
    }
    getAuthorColor(author) {
        const colors = ['#b5082e', '#2e97d3', '#bb00ff', '#f37e43', '#03a60b', '#881824', '#e09a2b', '#50565e', '#1f7a8c',
            '#7b5ea7', '#2db36c', '#d9480f', '#0a58ca', '#a83279', '#00897b'];
        const authorStr = (author ? author : 'Guest User').trim().toLowerCase();
        let hash = 5381;
        for (let i = 0; i < authorStr.length; i++) {
            const code = authorStr.charCodeAt(i);
            hash = (hash * 33 + code) % 4294967296;
        }
        const idx = Math.abs(hash) % colors.length;
        return colors[idx];
    }
    getAvatarInitials(name) {
        const parts = (name ? name : 'Guest User').trim().split(' ');
        if (parts && parts.length === 1) {
            return parts[0].charAt(0).toUpperCase();
        }
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    renderHeaderActions(headerWrap, initial, address, thread) {
        const l10n = this.parent.serviceLocator.getService(locale);
        const parentHeader = headerWrap.closest('.e-comment-header');
        const existingActions = headerWrap.querySelector('.e-comment-actions');
        if (existingActions) {
            existingActions.remove();
        }
        if (initial) {
            if (parentHeader) {
                const existingCloseBtn = parentHeader.querySelector('.e-comment-cancel');
                if (existingCloseBtn) {
                    existingCloseBtn.remove();
                }
            }
            const cancelBtn = this.parent.createElement('button', {
                className: 'e-comment-cancel',
                attrs: { 'aria-label': 'Cancel', title: l10n.getConstant('Cancel') }
            });
            const button = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-close' }, cancelBtn);
            button.createElement = this.parent.createElement;
            EventHandler.add(button.element, 'click', () => this.handleInitialCancel(cancelBtn), this);
            headerWrap.appendChild(cancelBtn);
            return;
        }
        if (parentHeader) {
            const existingCloseBtn = parentHeader.querySelector('.e-comment-cancel');
            if (existingCloseBtn) {
                existingCloseBtn.remove();
            }
        }
        const actionsWrap = this.parent.createElement('div', { className: 'e-comment-actions' });
        if (this.isEditing) {
            headerWrap.appendChild(actionsWrap);
            return;
        }
        if (thread && thread.isResolved === true) {
            const reopenBtnEl = this.parent.createElement('button', {
                className: 'e-comment-reopen-btn',
                attrs: { type: 'button', title: l10n.getConstant('Reopen') }
            });
            const reopenBtn = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-undo' }, reopenBtnEl);
            reopenBtn.createElement = this.parent.createElement;
            EventHandler.add(reopenBtn.element, 'click', () => this.setThreadResolved(false, reopenBtnEl), this);
            const deleteBtnEl = this.parent.createElement('button', {
                className: 'e-comment-delete',
                attrs: { type: 'button', title: l10n.getConstant('DeleteThread') }
            });
            const deleteBtn = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-trash' }, deleteBtnEl);
            deleteBtn.createElement = this.parent.createElement;
            EventHandler.add(deleteBtn.element, 'click', () => this.deleteComment({ sourceEl: deleteBtnEl }), this);
            actionsWrap.appendChild(reopenBtnEl);
            actionsWrap.appendChild(deleteBtnEl);
            headerWrap.appendChild(actionsWrap);
            return;
        }
        const cellRef = this.parent.createElement('span', { className: 'e-comment-cellref' });
        cellRef.textContent = address;
        actionsWrap.appendChild(cellRef);
        const menuBar = this.parent.createElement('button', {
            className: 'e-comment-menu e-flat', attrs: { type: 'button', title: l10n.getConstant('ThreadAction') }
        });
        const userOption = [
            { text: l10n.getConstant('EditComment'), iconCss: 'e-icons e-edit' },
            { text: l10n.getConstant('ResolveThread'), iconCss: 'e-icons e-check' },
            { text: l10n.getConstant('DeleteThread'), iconCss: 'e-icons e-trash' }
        ];
        const menuItem = new DropDownButton({
            items: userOption,
            createPopupOnClick: true,
            iconCss: 'e-icons e-more-horizontal-1',
            cssClass: 'e-caret-hide e-menu-popup',
            enableRtl: this.parent.enableRtl,
            select: (e) => this.onThreadMenuSelect(e, menuBar),
            open: () => this.setPopupPosition(menuItem, menuBar)
        });
        menuItem.createElement = this.parent.createElement;
        menuItem.appendTo(menuBar);
        actionsWrap.appendChild(menuBar);
        headerWrap.appendChild(actionsWrap);
    }
    setPopupPosition(ddb, triggerEl) {
        if (ddb && ddb.dropDown) {
            const popupEl = ddb.dropDown.element;
            if (popupEl) {
                const btnRect = triggerEl.getBoundingClientRect();
                const popupRect = popupEl.getBoundingClientRect();
                popupEl.style.left = `${this.parent.enableRtl ? btnRect.left : btnRect.right - popupRect.width}px`;
            }
        }
    }
    handleInitialCancel(ele) {
        if (ele) {
            const container = this.getContainer(ele);
            const indices = this.getIndexesFromContainer(container);
            const sheet = this.parent.getActiveSheet();
            if (indices) {
                const [rowIndex, columnIndex] = indices;
                const cell = getCell(rowIndex, columnIndex, sheet);
                if (!cell || !cell.comment) {
                    this.detachCommentIndicator(rowIndex, columnIndex);
                }
            }
            if (container) {
                EventHandler.remove(container, 'mouseout', this.mouseOut);
                this.unbindContainerEvents(container);
                const host = this.getBodyHost(container);
                if (host) {
                    this.unbindReplyHover(host);
                }
                detach(container);
                if (this.isReviewPaneVisible && container.classList.contains('e-thread-draft')) {
                    if (!sheet.comments || !sheet.comments.length) {
                        this.renderReviewBody();
                    }
                }
            }
            if (this.activeReplyDdb) {
                this.activeReplyDdb.destroy();
                this.activeReplyDdb = null;
            }
            this.isCommentVisible = false;
            this.activeCommentCell = null;
            this.isEditing = false;
        }
    }
    onThreadMenuSelect(event, sourceEl) {
        const l10n = this.parent.serviceLocator.getService(locale);
        const text = event.item.text;
        if (text === l10n.getConstant('EditComment')) {
            this.beginEdit(false, undefined, sourceEl);
        }
        else if (text === l10n.getConstant('ResolveThread')) {
            this.setThreadResolved(true, sourceEl);
        }
        else {
            this.deleteComment({ sourceEl: sourceEl });
        }
    }
    createBodyContent(thread, rebuild, inPane, cId) {
        if (thread && thread.createdTime instanceof Date) {
            thread.createdTime = getISOTime(thread.createdTime);
            if (thread.replies) {
                thread.replies.forEach((reply) => {
                    if (reply.createdTime && reply.createdTime instanceof Date) {
                        reply.createdTime = getISOTime(reply.createdTime);
                    }
                });
            }
        }
        const dataSource = thread ? this.convertThreadToListItems(thread) : [];
        if (inPane) {
            const host = this.parent.createElement('div', { className: 'e-comment-body' });
            if (Browser.isDevice) {
                host.classList.add('e-device-comment');
            }
            this.initListView(host, dataSource, true);
            if (!thread.isResolved && !Browser.isDevice) {
                this.bindReplyHover(host, cId);
            }
            return host;
        }
        if (!this.bodyHost) {
            this.bodyHost = this.parent.createElement('div', { className: 'e-comment-body' });
        }
        if (!this.commentListView) {
            this.commentListView = this.initListView(this.bodyHost, dataSource, false);
        }
        else if (rebuild) {
            this.commentListView.setProperties({ dataSource }, true);
            this.commentListView.refresh();
        }
        if (!thread.isResolved && !Browser.isDevice) {
            this.bindReplyHover(this.bodyHost);
        }
        return this.bodyHost;
    }
    initListView(host, dataSource, registerForCleanup) {
        const itemTemplate = (data) => {
            const l10n = this.parent.serviceLocator.getService(locale);
            const actionText = l10n.getConstant('CommentAction');
            const root = this.parent.createElement('div', {
                className: `e-comment-item ${data.type}`,
                attrs: Object.assign({ 'data-id': data.id }, (data.type === 'reply' ? { 'data-reply-id': data.id } : {}))
            });
            const renderTextAndTimestamp = (text, createdTime, parent) => {
                const textEl = this.parent.createElement('div', { className: 'e-comment-text' });
                textEl.textContent = text;
                const tsEl = this.parent.createElement('span', { className: 'e-comment-timestamp' });
                tsEl.textContent = createdTime ? new Date(createdTime + 'z').toLocaleDateString(this.parent.locale, {
                    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                }) : '';
                parent.appendChild(textEl);
                parent.appendChild(tsEl);
            };
            if (data.type === 'initial') {
                renderTextAndTimestamp(data.text, data.createdTime, root);
                return [root];
            }
            const row = this.parent.createElement('div', { className: 'e-comment-reply-row' });
            const avatarWrap = this.parent.createElement('div', { className: 'e-comment-avatar-wrap' });
            const authorName = data.author;
            const avatarEl = this.getAvatar(authorName);
            avatarWrap.appendChild(avatarEl);
            const col = this.parent.createElement('div', { className: 'e-comment-reply-col' });
            const header = this.parent.createElement('div', { className: 'e-comment-header-wrap' });
            const titleWrap = this.parent.createElement('div', { className: 'e-comment-title-wrap' });
            const title = this.parent.createElement('div', { className: 'e-comment-title' });
            title.textContent = authorName;
            titleWrap.appendChild(title);
            const menuBtn = this.parent.createElement('button', {
                className: 'e-reply-ddb e-flat',
                attrs: { type: 'button', 'data-reply-id': data.id, 'aria-label': actionText }
            });
            if (Browser.isDevice) {
                this.renderReplyDdb(menuBtn);
            }
            header.appendChild(titleWrap);
            header.appendChild(menuBtn);
            const content = this.parent.createElement('div', { className: 'e-comment-reply-content' });
            renderTextAndTimestamp(data.text, data.createdTime, content);
            col.appendChild(header);
            col.appendChild(content);
            row.appendChild(avatarWrap);
            row.appendChild(col);
            root.appendChild(row);
            return [root];
        };
        enableRipple(false);
        const listView = new ListView({
            dataSource: dataSource,
            template: itemTemplate,
            cssClass: 'e-comment-listview',
            enableRtl: this.parent.enableRtl,
            enableVirtualization: true,
            height: '100%',
            select: (args) => {
                if (this.isReviewPaneVisible) {
                    this.updateCellSelction(args);
                }
            }
        });
        listView.createElement = this.parent.createElement;
        /* eslint-disable */
        listView.isInternalTemplate = true;
        /* eslint-enable */
        listView.appendTo(host);
        if (registerForCleanup) {
            this.reviewInstances.push({ destroy: () => { listView.destroy(); } });
        }
        return listView;
    }
    renderReplyDdb(btn) {
        if (this.isEditing) {
            btn.style.visibility = 'hidden';
            return;
        }
        if (btn.dataset.ddbMounted === 'true') {
            return;
        }
        const l10n = this.parent.serviceLocator.getService(locale);
        const items = [
            { id: 'edit-reply', text: l10n.getConstant('EditComment'), iconCss: 'e-icons e-edit' },
            { id: 'delete-reply', text: l10n.getConstant('DeleteComment'), iconCss: 'e-icons e-trash' }
        ];
        const ddb = new DropDownButton({
            items: items,
            createPopupOnClick: true,
            iconCss: 'e-icons e-more-horizontal-1',
            cssClass: 'e-caret-hide e-menu-popup',
            enableRtl: this.parent.enableRtl,
            select: (args) => {
                const replyId = btn.getAttribute('data-reply-id') || '';
                this.onReplyMenuSelect(args, replyId, btn);
            },
            open: () => this.setPopupPosition(ddb, btn)
        });
        ddb.createElement = this.parent.createElement;
        ddb.appendTo(btn);
        btn.dataset.ddbMounted = 'true';
        this.activeReplyDdb = ddb;
    }
    onReplyMenuSelect(args, replyId, sourceEl) {
        const l10n = this.parent.serviceLocator.getService(locale);
        const text = args.item.text;
        if (text === l10n.getConstant('EditComment')) {
            this.beginEdit(true, replyId, sourceEl);
        }
        else {
            this.deleteReplyById(replyId, sourceEl);
        }
    }
    createFooterContent(rowIdx, colIdx, initial) {
        const l10n = this.parent.serviceLocator.getService(locale);
        const footer = this.parent.createElement('div', { className: 'e-comment-footer' });
        const cell = getCell(rowIdx, colIdx, this.parent.getActiveSheet());
        if (cell && cell.comment && cell.comment.isResolved === true) {
            return footer;
        }
        footer.setAttribute('data-mode', initial ? 'initial' : 'reply');
        const textArea = this.parent.createElement('textarea', {
            className: 'e-comment-input',
            attrs: { placeholder: initial ? l10n.getConstant('AddComment') : l10n.getConstant('Reply'), rows: '1', name: 'footerTextArea' }
        });
        textArea.style.height = '32px';
        const btnBar = this.parent.createElement('div', { className: 'e-comment-btn' });
        footer.appendChild(textArea);
        footer.appendChild(btnBar);
        if (initial) {
            this.ensureFooterButtons(footer, initial);
            this.syncFooterPostState(footer, textArea);
        }
        this.wireFooterEvents(footer);
        return footer;
    }
    convertThreadToListItems(thread) {
        const items = [];
        items.push({
            id: thread.id, type: 'initial', author: thread.author || 'Guest User',
            text: thread.text, createdTime: thread.createdTime
        });
        const replies = thread.replies || [];
        if (replies.length > 0) {
            replies.forEach((reply) => {
                items.push({
                    id: reply.id || getUniqueID('e_spreadsheet_reply'), type: 'reply',
                    author: reply.author || 'Guest User', text: reply.text, createdTime: reply.createdTime
                });
            });
        }
        return items;
    }
    postComment(rowIdx, colIdx, commentText, container) {
        if (!commentText) {
            return;
        }
        const timeStampValue = getISOTime();
        const actionFromPanel = container && container.classList.contains('e-pane-container');
        const cell = getCell(rowIdx, colIdx, this.parent.getActiveSheet());
        if (cell && cell.comment) {
            const thread = JSON.parse(JSON.stringify(cell.comment));
            if (!Array.isArray(thread.replies)) {
                thread.replies = [];
            }
            const reply = {
                id: getUniqueID('e_spreadsheet_reply'),
                author: this.parent.author || 'Guest User',
                text: commentText,
                createdTime: timeStampValue
            };
            thread.replies.push(reply);
            this.saveComment(rowIdx, colIdx, thread, 'addReply', actionFromPanel);
            const body = container.querySelector('.e-comment-body');
            let targetListView;
            if (body) {
                targetListView = getComponent(body, 'listview');
            }
            else {
                targetListView = this.commentListView;
            }
            if (targetListView) {
                const avatarEl = this.getAvatar(reply.author);
                targetListView.addItem([{
                        id: reply.id,
                        type: 'reply',
                        author: reply.author,
                        text: reply.text,
                        createdTime: reply.createdTime,
                        avatarHtml: avatarEl.outerHTML
                    }]);
                if (body) {
                    body.scrollTop = body.scrollHeight;
                }
            }
            return;
        }
        const id = getUniqueID('e_spreadsheet_comment');
        const newThread = {
            id: id,
            author: this.parent.author || 'Guest User',
            text: commentText,
            createdTime: timeStampValue,
            isResolved: false,
            replies: []
        };
        this.saveComment(rowIdx, colIdx, newThread, 'addComment', actionFromPanel);
        const header = container.querySelector('.e-comment-header');
        if (header && !header.querySelector('.e-comment-actions')) {
            const headerWrap = header.querySelector('.e-comment-header-wrap');
            if (headerWrap) {
                this.renderHeaderActions(headerWrap, false, getCellAddress(rowIdx, colIdx));
            }
        }
        if (container) {
            const body = this.createBodyContent(newThread, true, this.isReviewPaneVisible);
            const footer = container.querySelector('.e-comment-footer');
            if (footer && body && body.parentElement !== container) {
                container.insertBefore(body, footer);
            }
            else if (footer && body && footer.previousElementSibling !== body) {
                container.insertBefore(body, footer);
            }
        }
        if (this.isReviewPaneVisible) {
            if (container && container.classList.contains('e-thread-draft')) {
                this.destoryCommentDdb(container);
                container.remove();
            }
        }
    }
    beginEdit(isReply, replyId, sourceEl) {
        const container = this.getContainer(sourceEl);
        const localBodyHost = this.getBodyHost(container);
        if (this.isEditing || !container || !localBodyHost) {
            return;
        }
        const idx = this.getIndexesFromContainer(container);
        if (idx) {
            const cell = getCell(idx[0], idx[1], this.parent.getActiveSheet());
            const th = cell && cell.comment;
            if (th && th.isResolved) {
                return;
            }
        }
        const itemSelector = isReply ? `.e-comment-item.reply[data-reply-id="${replyId}"]` : '.e-comment-item.initial';
        const itemEl = localBodyHost.querySelector(itemSelector);
        if (!itemEl) {
            return;
        }
        const textEl = itemEl.querySelector('.e-comment-text');
        if (!textEl) {
            return;
        }
        const originalText = textEl.textContent || '';
        const l10n = this.parent.serviceLocator.getService(locale);
        const editorWrap = this.parent.createElement('div', { className: 'e-comment-edit-wrap' });
        const textArea = this.parent.createElement('textarea', {
            className: 'e-comment-input e-comment-edit-input',
            attrs: { rows: '1', name: 'editTextArea' }
        });
        EventHandler.add(textArea, 'focus', () => { textArea.classList.add('active'); }, this);
        EventHandler.add(textArea, 'blur', () => { textArea.classList.remove('active'); }, this);
        textArea.value = originalText;
        this.adjustTextareaHeight(textArea, true);
        const tsEle = itemEl.querySelector('.e-comment-timestamp');
        let tsValue = '';
        if (tsEle) {
            tsValue = tsEle.style.display;
            tsEle.style.display = 'none';
        }
        const btnBar = this.parent.createElement('div', { className: 'e-comment-btn' });
        const postBtnEl = this.parent.createElement('button', {
            className: 'e-comment-post',
            attrs: { type: 'button', 'aria-label': 'Post', title: l10n.getConstant('EditComment') }
        });
        const postBtn = new Button({ iconCss: 'e-icons e-send', isPrimary: true }, postBtnEl);
        postBtn.createElement = this.parent.createElement;
        const cancelBtnEl = this.parent.createElement('button', {
            className: 'e-comment-cancel',
            attrs: { type: 'button', 'aria-label': 'Cancel', title: l10n.getConstant('Cancel') }
        });
        const cancelBtn = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-close' }, cancelBtnEl);
        cancelBtn.createElement = this.parent.createElement;
        EventHandler.add(textArea, 'input', () => this.adjustTextareaHeight(textArea, true), this);
        const postCommentFn = () => {
            this.applyInlineEdit(isReply, textArea.value, textEl, editorWrap, originalText, replyId, container);
        };
        EventHandler.add(postBtn.element, 'click', postCommentFn, this);
        const cancelBtnFn = () => {
            textEl.textContent = originalText;
            textEl.style.display = '';
            editorWrap.remove();
            this.endEdit(container);
        };
        EventHandler.add(cancelBtn.element, 'click', cancelBtnFn, this);
        btnBar.appendChild(postBtnEl);
        btnBar.appendChild(cancelBtnEl);
        editorWrap.appendChild(textArea);
        editorWrap.appendChild(btnBar);
        textEl.style.display = 'none';
        if (textEl.parentElement) {
            textEl.parentElement.insertBefore(editorWrap, textEl.nextSibling);
        }
        this.editingState = {
            isReply, replyId, textEl, editorWrap, originalText, tsEle, tsValue,
            container, textHost: localBodyHost, addrIdx: idx
        };
        this.isEditing = true;
        this.setTextAreaState(container, true);
        container.classList.add('e-comment-editing');
        const header = container.querySelector('.e-comment-header');
        if (header) {
            const headerWrap = header.querySelector('.e-comment-header-wrap');
            if (headerWrap) {
                if (idx && idx.length === 2) {
                    this.renderHeaderActions(headerWrap, false, getCellAddress(idx[0], idx[1]));
                }
            }
        }
        const replyBtns = localBodyHost.querySelectorAll('.e-reply-ddb');
        if (replyBtns) {
            replyBtns.forEach((btn) => {
                btn.style.visibility = 'hidden';
            });
        }
        getUpdateUsingRaf(() => {
            this.adjustTextareaHeight(textArea, true);
            textArea.focus();
            const len = textArea.value.length;
            textArea.setSelectionRange(len, len);
        });
    }
    applyInlineEdit(isReply, newText, textEl, editorWrap, originalText, replyId, container) {
        const finalText = newText || originalText;
        textEl.textContent = finalText;
        textEl.style.display = '';
        editorWrap.remove();
        const indices = this.getIndexesFromContainer(container || null);
        if (indices && indices.length === 2) {
            const [row, col] = indices;
            const cell = getCell(row, col, this.parent.getActiveSheet());
            const actionFromPanel = container && container.classList.contains('e-pane-container');
            if (cell && cell.comment) {
                const thread = JSON.parse(JSON.stringify(cell.comment));
                if (isReply) {
                    if (!replyId || !thread.replies) {
                        this.endEdit(container);
                        return;
                    }
                    const idx = thread.replies.findIndex((r) => r.id === replyId);
                    if (idx > -1) {
                        thread.replies[idx] = Object.assign({}, thread.replies[idx], { text: finalText });
                        this.saveComment(row, col, thread, 'editReply', actionFromPanel);
                    }
                }
                else {
                    const updatedThread = Object.assign({}, thread, { text: finalText });
                    this.saveComment(row, col, updatedThread, 'editComment', actionFromPanel);
                }
            }
        }
        this.endEdit(container);
    }
    endEdit(container) {
        if (this.editingState && this.editingState.tsEle) {
            this.editingState.tsEle.style.display = isNullOrUndefined(this.editingState.tsValue) ? '' : this.editingState.tsValue;
        }
        const targetContainer = container || (this.editingState && this.editingState.container);
        if (targetContainer) {
            targetContainer.classList.remove('e-comment-editing');
            this.setTextAreaState(targetContainer, false);
            const header = targetContainer.querySelector('.e-comment-header');
            if (header) {
                const headerWrap = header.querySelector('.e-comment-header-wrap');
                if (headerWrap) {
                    const indices = this.getIndexesFromContainer(targetContainer);
                    if (indices && indices.length === 2) {
                        const prev = this.isEditing;
                        this.isEditing = false;
                        this.renderHeaderActions(headerWrap, false, getCellAddress(indices[0], indices[1]));
                        this.isEditing = prev;
                    }
                }
            }
            const localBodyHost = this.getBodyHost(targetContainer);
            if (localBodyHost) {
                const replyBtns = localBodyHost.querySelectorAll('.e-reply-ddb');
                replyBtns.forEach((btn) => {
                    btn.style.visibility = '';
                });
            }
        }
        this.isEditing = false;
        this.editingState = null;
    }
    deleteReplyById(replyId, sourceEl) {
        if (replyId && sourceEl) {
            const container = this.getContainer(sourceEl);
            const host = this.getBodyHost(container);
            if (container && host) {
                const indexes = this.getIndexesFromContainer(container);
                if (indexes && indexes.length === 2) {
                    const [row, col] = indexes;
                    const cell = getCell(row, col, this.parent.getActiveSheet());
                    if (cell && cell.comment) {
                        const thread = JSON.parse(JSON.stringify(cell.comment));
                        if (thread.replies) {
                            const idx = thread.replies.findIndex((r) => r.id === replyId);
                            if (idx > -1) {
                                thread.replies.splice(idx, 1);
                                this.saveComment(row, col, thread, 'deleteReply', container.classList.contains('e-pane-container'));
                                const targetListView = getComponent(host, 'listview');
                                if (targetListView) {
                                    targetListView.removeItem({ id: replyId });
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    setTextAreaState(container, disabled) {
        if (container) {
            const l10n = this.parent.serviceLocator.getService(locale);
            const textArea = container.querySelector('.e-comment-footer .e-comment-input');
            if (textArea) {
                if (disabled) {
                    textArea.disabled = true;
                    textArea.placeholder = l10n.getConstant('CommentEditingInProgress');
                }
                else {
                    textArea.disabled = false;
                    textArea.placeholder = l10n.getConstant('Reply');
                    this.adjustTextareaHeight(textArea, false);
                }
            }
        }
    }
    adjustTextareaHeight(textArea, isEdit) {
        if (textArea) {
            const cs = getComputedStyle(textArea);
            let linePx = parseFloat(cs.lineHeight);
            if (isNaN(linePx)) {
                const fontSize = parseFloat(cs.fontSize) || 14;
                linePx = Math.round(fontSize * 1.2);
            }
            const padTB = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
            const borderTB = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
            const minLines = 1;
            const maxLines = 5;
            if (!isEdit && textArea.value.trim().length === 0) {
                textArea.style.height = `${(minLines * linePx) + padTB + borderTB}px`;
                textArea.style.overflowY = 'hidden';
                return;
            }
            textArea.style.height = 'auto';
            const contentHeight = Math.max(0, textArea.scrollHeight - padTB);
            const neededLines = Math.max(1, Math.ceil(contentHeight / Math.max(1, linePx)));
            const clampedLines = Math.max(minLines, Math.min(maxLines, neededLines));
            const finalHeight = (clampedLines * linePx) + padTB + borderTB;
            textArea.style.height = `${finalHeight}px`;
            textArea.style.overflowY = 'hidden';
        }
    }
    saveComment(rIdx, cIdx, comment, actionName, isPanel) {
        if (comment && !comment.address) {
            comment.address = [rIdx, cIdx];
        }
        const sheet = this.parent.getActiveSheet();
        const address = getSheetName(this.parent, this.parent.activeSheetIndex) + '!' + getRangeAddress([rIdx, cIdx]);
        const eventArgs = { comment: JSON.parse(JSON.stringify(comment)), address: address, cancel: false };
        this.parent.notify(beginAction, { eventArgs: eventArgs, action: actionName });
        if (eventArgs.cancel) {
            return;
        }
        comment = JSON.parse(JSON.stringify(eventArgs.comment));
        this.processSheetComments({ sheet: sheet, comment: comment, isDelete: false });
        const cancel = updateCell(this.parent, sheet, {
            rowIdx: rIdx, colIdx: cIdx, preventEvt: true, cell: { comment: comment }
        });
        if (!cancel) {
            const cell = getCell(rIdx, cIdx, sheet, null, true);
            cell.comment = comment;
            if (actionName === 'addComment') {
                this.parent.setUsedRange(rIdx, cIdx, sheet);
            }
            const td = this.parent.getCell(rIdx, cIdx);
            if (td && !td.querySelector('.e-comment-indicator')) {
                this.createCommentIndicator({ targetEle: td, rIdx: rIdx, cIdx: cIdx });
            }
            if (this.isReviewPaneVisible) {
                if (actionName === 'addComment') {
                    this.insertThreadIntoPanel(comment);
                    this.scrollToThreadInPanel(comment.id);
                }
                else if (!isPanel && actionName !== 'addComment') {
                    this.syncThreadInPanel(comment);
                }
            }
            this.parent.notify(completeAction, { eventArgs: eventArgs, action: actionName });
        }
    }
    detachCommentIndicator(rowIdx, colIdx) {
        const cellElement = this.parent.getCell(rowIdx, colIdx);
        if (cellElement) {
            const indicator = cellElement.querySelector('.e-comment-indicator');
            if (indicator) {
                EventHandler.remove(indicator, 'mouseover', this.mouseOver);
                EventHandler.remove(indicator, 'mouseout', this.mouseOut);
                delete indicator.dataset.commentListenersAdded;
                delete indicator.dataset.commentRowIndex;
                delete indicator.dataset.commentColIndex;
                detach(indicator);
            }
        }
    }
    setCommentContainerPosition(commentContainer, cellRect) {
        const selectAllCell = this.parent.element.getElementsByClassName('e-select-all-cell')[0];
        const scroller = this.parent.element.getElementsByClassName('e-scroller')[0];
        const sheetClientRect = this.parent.element.getElementsByClassName('e-sheet')[0].getBoundingClientRect();
        const elementClientRect = this.parent.element.getBoundingClientRect();
        const elementPosition = this.parent.element.style.getPropertyValue('position');
        const offsetTop = (elementPosition === 'absolute' ? 0 : this.parent.element.offsetTop);
        const offsetLeft = (elementPosition === 'absolute' ? 0 : this.parent.element.offsetLeft);
        commentContainer.style.position = 'absolute';
        let topPos = cellRect.top - (elementClientRect.top - offsetTop);
        if (!isNullOrUndefined(scroller)) {
            const spaceToBottom = scroller.getBoundingClientRect().top - (cellRect.top);
            if (spaceToBottom < commentContainer.offsetHeight) {
                topPos -= (commentContainer.offsetHeight - spaceToBottom + 5);
            }
            else if (!isNullOrUndefined(selectAllCell) && cellRect.top < selectAllCell.getBoundingClientRect().bottom) {
                topPos = selectAllCell.getBoundingClientRect().bottom - (elementClientRect.top - offsetTop) + 5;
            }
        }
        if (topPos < 0) {
            topPos = 5;
        }
        commentContainer.style.top = `${topPos}px`;
        let leftPos;
        const preferredWidth = 244;
        let actualWidth = preferredWidth;
        const spaceToRight = sheetClientRect.width - (cellRect.left + cellRect.width - sheetClientRect.left);
        if (spaceToRight > preferredWidth + 5) {
            leftPos = cellRect.left + cellRect.width - (elementClientRect.left - offsetLeft);
        }
        else {
            leftPos = cellRect.left - (elementClientRect.left - offsetLeft) - preferredWidth;
            if (leftPos < 0) {
                leftPos = 5;
                actualWidth = elementClientRect.width - 10;
            }
        }
        commentContainer.style.left = `${leftPos}px`;
        commentContainer.style.width = `${actualWidth}px`;
    }
    deleteComment(args) {
        let container;
        let rIdx;
        let cIdx;
        if (args && args.sourceEl) {
            container = this.getContainer(args.sourceEl);
        }
        if (args && !isNullOrUndefined(args.rowIndex) && !isNullOrUndefined(args.columnIndex)) {
            rIdx = args.rowIndex;
            cIdx = args.columnIndex;
        }
        else {
            const index = this.getIndexesFromContainer(container);
            rIdx = index[0];
            cIdx = index[1];
        }
        const sheet = this.parent.getActiveSheet();
        const cell = getCell(rIdx, cIdx, sheet);
        this.detachCommentIndicator(rIdx, cIdx);
        if (!isNullOrUndefined(cell) && cell.comment) {
            const address = getSheetName(this.parent, this.parent.activeSheetIndex) + '!' +
                getRangeAddress([rIdx, cIdx]);
            const eventArgs = { comment: cell.comment, address: address, cancel: false };
            this.parent.notify(beginAction, { eventArgs: eventArgs, action: 'deleteComment' });
            if (eventArgs.cancel) {
                return;
            }
            this.processSheetComments({ sheet: sheet, id: cell.comment.id, isDelete: true });
            delete cell.comment;
            updateCell(this.parent, sheet, { rowIdx: rIdx, colIdx: cIdx, preventEvt: true, cell: cell });
            if (this.isReviewPaneVisible) {
                this.refreshReviewListFromSheet();
            }
            eventArgs.comment = null;
            this.parent.notify(completeAction, { eventArgs: eventArgs, action: 'deleteComment' });
        }
        if (container) {
            EventHandler.remove(container, 'mouseout', this.mouseOut);
            this.unbindContainerEvents(container);
            const host = this.getBodyHost(container);
            if (host) {
                this.unbindReplyHover(host);
                const lv = getComponent(host, 'listview');
                if (lv) {
                    lv.destroy();
                }
            }
            const footerEl = container.querySelector('.e-comment-footer');
            if (footerEl) {
                this.unwireFooterEvents(footerEl);
                this.removeFooterButtons(footerEl);
            }
            if (this.activeReplyDdb) {
                this.activeReplyDdb.destroy();
                this.activeReplyDdb = null;
            }
            this.destoryCommentDdb(container);
            detach(container);
        }
        if (this.isEditing) {
            this.isEditing = false;
            this.editingState = null;
        }
    }
    replyToComment() {
        this.initiateComment();
        const container = this.getCommentContainer();
        if (container) {
            const textArea = container.querySelector('.e-comment-footer .e-comment-input');
            if (textArea) {
                textArea.focus();
                textArea.select();
                this.adjustTextareaHeight(textArea, false);
                if (!container.classList.contains('active')) {
                    container.classList.add('active');
                }
            }
        }
    }
    setThreadResolved(resolved, sourceEl) {
        const container = this.getContainer(sourceEl);
        const indexes = this.getIndexesFromContainer(container);
        if (indexes) {
            const [rowIndex, columnIndex] = indexes;
            const cell = getCell(rowIndex, columnIndex, this.parent.getActiveSheet());
            if (cell && cell.comment) {
                const thread = JSON.parse(JSON.stringify(cell.comment));
                if (thread.isResolved !== resolved) {
                    const updated = Object.assign({}, thread, { isResolved: resolved });
                    const actionFromPanel = container && container.classList.contains('e-pane-container');
                    this.saveComment(rowIndex, columnIndex, updated, resolved ? 'resolveComment' : 'reopenComment', actionFromPanel);
                    this.renderThreadContainer(rowIndex, columnIndex, updated, container);
                    if (this.isReviewPaneVisible && this.reviewFilter !== 'all') {
                        if (!this.filterMatches(updated)) {
                            this.removeThreadFromPanel(updated.id);
                        }
                    }
                }
            }
        }
    }
    renderThreadContainer(rIdx, cIdx, thread, container) {
        if (container) {
            const address = getCellAddress(rIdx, cIdx);
            const oldHeader = container.querySelector('.e-comment-header');
            const newHeader = this.createheaderContent(false, address, thread);
            if (oldHeader && newHeader) {
                oldHeader.replaceWith(newHeader);
            }
            const bodyEl = container.querySelector('.e-comment-body');
            const existingFooter = container.querySelector('.e-comment-footer');
            if (thread.isResolved) {
                if (existingFooter) {
                    this.unwireFooterEvents(existingFooter);
                    this.removeFooterButtons(existingFooter);
                    existingFooter.remove();
                }
                container.classList.add('e-thread-resolved');
                this.removeReplyButtons(bodyEl);
                this.setTextAreaState(container, true);
                this.renderResolvedWrap(container, rIdx, cIdx, thread.author || (this.parent.author || 'Guest User'));
            }
            else {
                if (!existingFooter) {
                    const newFooter = this.createFooterContent(rIdx, cIdx, false);
                    if (newFooter) {
                        if (bodyEl && bodyEl.nextSibling) {
                            container.insertBefore(newFooter, bodyEl.nextSibling);
                        }
                        else {
                            container.appendChild(newFooter);
                        }
                    }
                }
                container.classList.remove('e-thread-resolved');
                this.addReplyButtons(bodyEl);
                this.setTextAreaState(container, false);
                ['.e-comment-resolved-wrap', '.e-comment-divider'].forEach((selector) => {
                    const element = container.querySelector(selector);
                    if (element) {
                        element.remove();
                    }
                });
            }
        }
    }
    renderResolvedContent(address, author) {
        const resolvedHeader = this.parent.createElement('div', { className: 'e-comment-resolved-wrap e-comment-header' });
        const avatar = this.getAvatar(author);
        const headerWrap = this.parent.createElement('div', { className: 'e-comment-header-wrap' });
        const titleWrap = this.parent.createElement('div', { className: 'e-comment-title-wrap' });
        const titleEl = this.parent.createElement('div', { className: 'e-comment-title' });
        titleEl.textContent = author || (this.parent.author || 'Guest User');
        const cellRefEl = this.parent.createElement('div', { className: 'e-comment-cellref' });
        cellRefEl.textContent = address;
        titleWrap.appendChild(titleEl);
        headerWrap.appendChild(titleWrap);
        headerWrap.appendChild(cellRefEl);
        resolvedHeader.appendChild(avatar);
        resolvedHeader.appendChild(headerWrap);
        return resolvedHeader;
    }
    renderResolvedWrap(container, rIdx, cIdx, author) {
        if (!container || container.querySelector('.e-comment-resolved-wrap')) {
            return;
        }
        const bodyEl = container.querySelector('.e-comment-body');
        if (bodyEl) {
            const divider = this.parent.createElement('div', { className: 'e-comment-divider' });
            const wrap = this.renderResolvedContent(getCellAddress(rIdx, cIdx), author);
            container.insertBefore(divider, bodyEl);
            container.insertBefore(wrap, bodyEl);
        }
    }
    getContainer(sourceEl) {
        return sourceEl && sourceEl.closest('.e-comment-container, .e-pane-container');
    }
    getBodyHost(container) {
        return container && container.querySelector('.e-comment-body');
    }
    getCommentContainer() {
        return this.parent.element.querySelector('.e-comment-container');
    }
    getIndexesFromContainer(container) {
        let index;
        if (container) {
            const cellRefEl = container.querySelector('.e-comment-cellref');
            const refText = cellRefEl && cellRefEl.textContent ? cellRefEl.textContent.trim() : '';
            if (refText) {
                index = getCellIndexes(refText);
            }
            else if (this.editingState && this.editingState.container === container && Array.isArray(this.editingState.addrIdx)) {
                index = this.editingState.addrIdx.slice();
            }
        }
        if (!index && this.activeCommentCell && this.activeCommentCell.length === 2) {
            index = [this.activeCommentCell[0], this.activeCommentCell[1]];
        }
        else if (!index) {
            index = getCellIndexes(this.parent.getActiveSheet().activeCell);
        }
        return index;
    }
    // Events handling for comment containers
    bindContainerEvents(container) {
        if (container) {
            EventHandler.remove(container, 'focusin', this.onContainerFocusIn);
            EventHandler.add(container, 'focusin', this.onContainerFocusIn, this);
            EventHandler.remove(container, 'focusout', this.onContainerFocusOut);
            EventHandler.add(container, 'focusout', this.onContainerFocusOut, this);
        }
    }
    unbindContainerEvents(container) {
        if (container) {
            EventHandler.remove(container, 'focusin', this.onContainerFocusIn);
            EventHandler.remove(container, 'focusout', this.onContainerFocusOut);
        }
    }
    onContainerFocusIn(evt) {
        const container = evt.currentTarget;
        if (container && !container.classList.contains('active')) {
            container.classList.add('active');
        }
    }
    onContainerFocusOut(evt) {
        const container = evt.currentTarget;
        const related = evt.relatedTarget;
        if (container) {
            if (!related || !container.contains(related)) {
                if (container.classList.contains('active')) {
                    container.classList.remove('active');
                }
            }
        }
    }
    bindReplyHover(host, containerId) {
        if (host) {
            if (containerId) {
                host.setAttribute('data-reply-container-id', containerId);
            }
            EventHandler.remove(host, 'mouseover', this.onReplyHoverMouseOver);
            EventHandler.add(host, 'mouseover', this.onReplyHoverMouseOver, this);
            EventHandler.remove(host, 'focusin', this.onReplyHoverFocusIn);
            EventHandler.add(host, 'focusin', this.onReplyHoverFocusIn, this);
        }
    }
    unbindReplyHover(host) {
        if (host) {
            EventHandler.remove(host, 'mouseover', this.onReplyHoverMouseOver);
            EventHandler.remove(host, 'focusin', this.onReplyHoverFocusIn);
        }
    }
    onReplyHoverMouseOver(evt) {
        this.handleReplyHover(evt.target);
    }
    onReplyHoverFocusIn(evt) {
        this.handleReplyHover(evt.target);
    }
    handleReplyHover(target) {
        if (this.isEditing) {
            return;
        }
        const targetEl = target;
        if (!targetEl) {
            return;
        }
        let container;
        if (this.isReviewPaneVisible) {
            const bodyHost = targetEl.closest('.e-comment-body');
            if (bodyHost) {
                const id = bodyHost.getAttribute('data-reply-container-id');
                if (id) {
                    container = this.parent.element.querySelector('.' + id);
                }
            }
        }
        else {
            container = this.getCommentContainer();
        }
        if (container && container.classList.contains('e-thread-resolved')) {
            return;
        }
        const btn = targetEl.querySelector('.e-reply-ddb');
        if (btn) {
            this.renderReplyDdb(btn);
        }
    }
    removeReplyButtons(bodyEl) {
        const buttons = bodyEl.querySelectorAll('.e-reply-ddb');
        buttons.forEach((btn) => {
            const ddb = getComponent(btn, 'dropdown-btn');
            if (ddb) {
                ddb.destroy();
            }
            btn.remove();
        });
    }
    addReplyButtons(bodyEl) {
        const l10n = this.parent.serviceLocator.getService(locale);
        const actionText = l10n.getConstant('CommentAction');
        const replyItems = bodyEl.querySelectorAll('.e-comment-item.reply');
        replyItems.forEach((item) => {
            const replyId = item.getAttribute('data-reply-id') || item.getAttribute('data-id') || '';
            const headerWrap = item.querySelector('.e-comment-header-wrap');
            if (headerWrap) {
                if (headerWrap.querySelector('.e-reply-ddb')) {
                    return;
                }
                const menuBtn = this.parent.createElement('button', {
                    className: 'e-reply-ddb e-flat',
                    attrs: { type: 'button', 'data-reply-id': replyId, 'aria-label': actionText }
                });
                headerWrap.appendChild(menuBtn);
                this.renderReplyDdb(menuBtn);
            }
        });
    }
    onFooterInput(e) {
        const textArea = e.currentTarget;
        const footer = textArea.closest('.e-comment-footer');
        if (footer) {
            if (textArea.value.trim().length > 0) {
                this.ensureFooterButtons(footer, footer.getAttribute('data-mode') === 'initial');
            }
            else if (document.activeElement !== textArea) {
                this.removeFooterButtons(footer);
            }
            this.adjustTextareaHeight(textArea, false);
            this.syncFooterPostState(footer, textArea);
        }
    }
    onFooterFocus(e) {
        const textArea = e.currentTarget;
        textArea.classList.add('active');
        const footer = textArea.closest('.e-comment-footer');
        if (footer) {
            this.ensureFooterButtons(footer, footer.getAttribute('data-mode') === 'initial');
            this.syncFooterPostState(footer, textArea);
        }
    }
    onFooterBlur(e) {
        const textArea = e.currentTarget;
        textArea.classList.remove('active');
        const footer = textArea.closest('.e-comment-footer');
        if (footer) {
            if (textArea.value.trim().length === 0) {
                this.removeFooterButtons(footer);
                textArea.style.height = '32px';
            }
        }
    }
    onFooterClick(e) {
        const target = e.target;
        const btn = target.closest('.e-comment-post, .e-comment-cancel');
        if (btn) {
            const footer = btn.closest('.e-comment-footer');
            const container = btn.closest('.e-comment-container, .e-pane-container');
            if (!footer || !container) {
                return;
            }
            const textArea = footer.querySelector('.e-comment-input');
            if (!textArea) {
                return;
            }
            this.isEditing = false;
            if (btn.classList.contains('e-comment-post')) {
                if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true') {
                    return;
                }
                const idx = this.getIndexesFromContainer(container);
                if (!Array.isArray(idx) || idx.length !== 2) {
                    return;
                }
                const [rIdx, cIdx] = idx;
                const text = (textArea.value || '');
                if (text.length === 0) {
                    return;
                }
                this.postComment(rIdx, cIdx, text, container);
                textArea.value = '';
                this.adjustTextareaHeight(textArea, false);
                this.syncFooterPostState(footer, textArea);
                if (footer.getAttribute('data-mode') === 'initial') {
                    footer.setAttribute('data-mode', 'reply');
                    const l10n = this.parent.serviceLocator.getService(locale);
                    textArea.placeholder = l10n.getConstant('Reply');
                }
                this.removeFooterButtons(footer);
            }
            else if (btn.classList.contains('e-comment-cancel')) {
                textArea.value = '';
                this.adjustTextareaHeight(textArea, false);
                this.removeFooterButtons(footer);
                this.syncFooterPostState(footer, textArea);
            }
        }
    }
    ensureFooterButtons(footer, initial) {
        const btnBar = footer.querySelector('.e-comment-btn');
        if (btnBar) {
            const l10n = this.parent.serviceLocator.getService(locale);
            if (!btnBar.querySelector('.e-comment-post')) {
                const postBtnEl = this.parent.createElement('button', {
                    className: 'e-comment-post',
                    attrs: { type: 'button', 'aria-label': 'Post', title: l10n.getConstant('Post') }
                });
                const postInstance = new Button({ iconCss: 'e-icons e-send', disabled: true }, postBtnEl);
                postInstance.createElement = this.parent.createElement;
                btnBar.appendChild(postBtnEl);
            }
            if (!initial && !btnBar.querySelector('.e-comment-cancel')) {
                const cancelBtnEl = this.parent.createElement('button', {
                    className: 'e-comment-cancel',
                    attrs: { type: 'button', 'aria-label': 'Cancel', title: l10n.getConstant('Cancel') }
                });
                const cancelInstance = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-close' }, cancelBtnEl);
                cancelInstance.createElement = this.parent.createElement;
                btnBar.appendChild(cancelBtnEl);
            }
        }
    }
    removeFooterButtons(footer) {
        const btnBar = footer.querySelector('.e-comment-btn');
        if (btnBar) {
            const post = btnBar.querySelector('.e-comment-post');
            const cancel = btnBar.querySelector('.e-comment-cancel');
            const destroyButton = (ele) => {
                requestAnimationFrame(() => {
                    const inst = getComponent(ele, 'btn');
                    if (inst) {
                        inst.destroy();
                    }
                    ele.remove();
                });
            };
            if (post) {
                destroyButton(post);
            }
            if (cancel) {
                destroyButton(cancel);
            }
        }
    }
    syncFooterPostState(footer, textArea) {
        const btnBar = footer.querySelector('.e-comment-btn');
        if (btnBar) {
            const postBtn = btnBar.querySelector('.e-comment-post');
            const hasContent = textArea.value.trim().length > 0;
            this.isEditing = hasContent;
            if (postBtn) {
                const inst = getComponent(postBtn, 'btn');
                if (inst) {
                    inst.disabled = !hasContent;
                    inst.isPrimary = hasContent;
                    inst.dataBind();
                    inst.element.style.opacity = hasContent ? '1' : '0.5';
                }
            }
        }
    }
    wireFooterEvents(footer) {
        if (!footer || footer.dataset.footerWired === '1') {
            return;
        }
        const textArea = footer.querySelector('.e-comment-input');
        const btnBar = footer.querySelector('.e-comment-btn');
        if (textArea) {
            EventHandler.add(textArea, 'input', this.onFooterInput, this);
            EventHandler.add(textArea, 'focus', this.onFooterFocus, this);
            EventHandler.add(textArea, 'blur', this.onFooterBlur, this);
        }
        if (btnBar) {
            EventHandler.add(btnBar, 'click', this.onFooterClick, this);
        }
        footer.dataset.footerWired = '1';
    }
    unwireFooterEvents(footer) {
        if (!footer || footer.dataset.footerWired !== '1') {
            return;
        }
        const textArea = footer.querySelector('.e-comment-input');
        const btnBar = footer.querySelector('.e-comment-btn');
        if (textArea) {
            EventHandler.remove(textArea, 'input', this.onFooterInput);
            EventHandler.remove(textArea, 'focus', this.onFooterFocus);
            EventHandler.remove(textArea, 'blur', this.onFooterBlur);
        }
        if (btnBar) {
            EventHandler.remove(btnBar, 'click', this.onFooterClick);
        }
        delete footer.dataset.footerWired;
    }
    commentHandler() {
        if (this.isReviewPaneVisible && this.parent.element.querySelector('.e-review-panel')) {
            const sheet = this.parent.getActiveSheet();
            const indexes = getCellIndexes(sheet.activeCell);
            const cell = getCell(indexes[0], indexes[1], sheet);
            const reviewHeaderNewBtn = this.parent.element.querySelector('.e-review-new');
            if (reviewHeaderNewBtn) {
                const newBtnObj = getComponent(reviewHeaderNewBtn, 'btn');
                if (newBtnObj) {
                    newBtnObj.disabled = !!(cell && cell.notes);
                    newBtnObj.dataBind();
                }
            }
            if (cell && cell.comment) {
                this.initiateComment({ rowIndex: indexes[0], columnIndex: indexes[1], isSelection: true });
            }
        }
    }
    removeCommentContainer() {
        const container = this.parent.element.querySelector('.e-comment-container');
        let rIdx;
        let cIdx;
        if (container) {
            if (container.dataset && container.dataset.commentRowIndex && container.dataset.commentColIndex) {
                rIdx = parseInt(container.dataset.commentRowIndex, 10);
                cIdx = parseInt(container.dataset.commentColIndex, 10);
            }
            const cellEle = this.parent.getCell(rIdx, cIdx);
            if (cellEle) {
                EventHandler.remove(cellEle, 'mouseout', this.mouseOut);
            }
            EventHandler.remove(container, 'mouseout', this.mouseOut);
            this.unbindContainerEvents(container);
            const host = this.getBodyHost(container);
            if (host) {
                this.unbindReplyHover(host);
            }
            const footerEl = container.querySelector('.e-comment-footer');
            if (footerEl) {
                this.unwireFooterEvents(footerEl);
                this.removeFooterButtons(footerEl);
            }
            this.destoryCommentDdb(container);
            detach(container);
            if (!isNullOrUndefined(rIdx) && !isNullOrUndefined(cIdx)) {
                const cell = getCell(rIdx, cIdx, this.parent.getActiveSheet());
                if (!cell || (cell && !cell.comment)) {
                    this.detachCommentIndicator(rIdx, cIdx);
                }
            }
            this.isCommentVisible = false;
            this.activeCommentCell = null;
            if (this.commentListView) {
                this.commentListView.destroy();
                this.commentListView = null;
            }
            this.bodyHost = null;
            this.isEditing = false;
        }
    }
    destoryCommentDdb(container) {
        [].slice.call(container.querySelectorAll('.e-comment-menu')).forEach((commentDdb) => {
            destroyComponent(commentDdb, DropDownButton);
        });
        [].slice.call(container.querySelectorAll('.e-reply-ddb')).forEach((replyDdb) => {
            destroyComponent(replyDdb, DropDownButton);
        });
    }
    processSheetComments(args) {
        const sheet = args.sheet;
        const id = args.id;
        const comment = args.comment;
        const isDelete = args.isDelete;
        if (!sheet.comments) {
            sheet.comments = [];
        }
        if (isDelete) {
            if (id) {
                const index = sheet.comments.findIndex((c) => c.id === id);
                if (index !== -1) {
                    sheet.comments.splice(index, 1);
                }
            }
        }
        else {
            if (comment) {
                if (!comment.id) {
                    comment.id = getUniqueID('e_spreadsheet_comment');
                }
                if (comment.replies && comment.replies.length > 0) {
                    comment.replies.forEach((reply) => {
                        if (!reply.id) {
                            reply.id = getUniqueID('e_spreadsheet_reply');
                        }
                    });
                }
                const existingIdx = sheet.comments.findIndex((c) => c.id === comment.id);
                if (existingIdx > -1) {
                    sheet.comments[existingIdx] = comment;
                }
                else {
                    const idx = getSortedIndex(sheet.comments, comment.address, true);
                    sheet.comments.splice(idx, 0, comment);
                }
            }
        }
        if (args.isRefresh && !isNullOrUndefined(args.sheetIdx)) {
            this.refreshCommentsPane({ sheetIdx: args.sheetIdx });
        }
    }
    updateCommentsFromSheet() {
        this.parent.sheets.forEach((sheet) => {
            if (sheet.comments) {
                sheet.comments.forEach((model) => {
                    if (!model.isResolved) {
                        model.isResolved = false;
                    }
                    const threadModel = model;
                    threadModel.id = getUniqueID('e_spreadsheet_comment');
                    if (threadModel.replies) {
                        threadModel.replies.forEach((reply) => {
                            reply.id = getUniqueID('e_spreadsheet_reply');
                        });
                    }
                    const indexes = threadModel.address;
                    setCell(indexes[0], indexes[1], sheet, { comment: threadModel }, true);
                });
            }
        });
    }
    showCommentPane(args) {
        const host = this.parent.element;
        const id = host.id;
        const sheetPanel = host.querySelector(`#${id}_sheet_panel`);
        const sheetEl = host.querySelector(`#${id}_sheet`);
        const sheetTabPanel = host.querySelector(`#${id}_sheet_tab_panel`);
        if (!sheetPanel || !sheetEl || !sheetTabPanel) {
            return;
        }
        this.parent.setProperties({ showCommentsPane: args.show }, true);
        let reviewPanel = sheetPanel.querySelector(`#${id}_review_panel`);
        if (!args.show) {
            if (this.reviewInstances && this.reviewInstances.length) {
                this.reviewInstances.forEach((inst) => inst.destroy());
                this.reviewInstances = [];
            }
            this.reviewHeaderEl = this.reviewBodyEl = null;
            if (this.scheduleMountId) {
                cancelAnimationFrame(this.scheduleMountId);
                this.scheduleMountId = 0;
            }
            if (reviewPanel && reviewPanel.parentElement) {
                reviewPanel.parentElement.removeChild(reviewPanel);
            }
            sheetEl.classList.remove('e-sheet-with-review-panel');
            sheetTabPanel.classList.remove('e-sheet-panel-with-review-panel');
            this.isReviewPaneVisible = false;
        }
        else {
            if (!reviewPanel) {
                reviewPanel = this.parent.createElement('div', { id: `${id}_review_panel`, className: 'e-review-panel' });
                sheetPanel.appendChild(reviewPanel);
            }
            sheetEl.classList.add('e-sheet-with-review-panel');
            sheetTabPanel.classList.add('e-sheet-panel-with-review-panel');
            reviewPanel.style.height = `calc(100% + ${getDPRValue(sheetTabPanel.getBoundingClientRect().height)}px)`;
            this.isReviewPaneVisible = true;
            this.buildReviewPanelUI(reviewPanel);
        }
        if (this.parent.element.getElementsByClassName('e-addNoteContainer').length) {
            this.parent.notify(updateNoteContainer, null);
        }
    }
    buildReviewPanelUI(reviewPanel) {
        reviewPanel.innerHTML = '';
        const wrap = this.parent.createElement('div', { className: 'e-review-wrap' });
        this.reviewHeaderEl = this.renderReviewHeader();
        wrap.appendChild(this.reviewHeaderEl);
        reviewPanel.appendChild(wrap);
        this.renderReviewBody();
    }
    renderReviewHeader() {
        const l10n = this.parent.serviceLocator.getService(locale);
        const header = this.parent.createElement('div', { className: 'e-review-header' });
        const wrap1 = this.parent.createElement('div', { className: 'e-review-header-wrap1' });
        const title = this.parent.createElement('div', { className: 'e-review-title' });
        title.textContent = l10n.getConstant('Comments');
        const closeBtnEl = this.parent.createElement('button', {
            className: 'e-review-close',
            attrs: { 'aria-label': 'Close', title: l10n.getConstant('Close') }
        });
        const closeBtn = new Button({ cssClass: 'e-flat', iconCss: 'e-icons e-close' }, closeBtnEl);
        closeBtn.createElement = this.parent.createElement;
        EventHandler.add(closeBtn.element, 'click', () => this.showCommentPane({ show: false }), this);
        this.reviewInstances.push(closeBtn);
        wrap1.appendChild(title);
        wrap1.appendChild(closeBtn.element);
        const wrap2 = this.parent.createElement('div', { className: 'e-review-header-wrap2' });
        const newBtnEl = this.parent.createElement('button', {
            className: 'e-review-new',
            attrs: { 'aria-label': 'New', title: l10n.getConstant('New') }
        });
        const newBtn = new Button({
            content: l10n.getConstant('New'), iconCss: 'e-icons e-comment-add',
            enableRtl: this.parent.enableRtl
        }, newBtnEl);
        newBtn.createElement = this.parent.createElement;
        EventHandler.add(newBtn.element, 'click', () => this.renderNewCommentForPanel(), this);
        this.reviewInstances.push(newBtn);
        const filterBtn = this.parent.createElement('button', {
            className: 'e-review-filter',
            attrs: { 'aria-label': 'Filter', title: l10n.getConstant('Filter') }
        });
        this.reviewFilterDdb = new DropDownButton({
            iconCss: this.getFilterIconCss(),
            enableRtl: this.parent.enableRtl,
            items: this.getFilterMenuItems(l10n),
            select: (e) => this.onFilterMenuSelect(e),
            beforeOpen: () => { this.reviewFilterDdb.setProperties({ items: this.getFilterMenuItems(l10n) }, true); },
            open: () => this.setPopupPosition(this.reviewFilterDdb, filterBtn)
        }, filterBtn);
        this.reviewFilterDdb.createElement = this.parent.createElement;
        this.refreshFilterButton();
        this.reviewInstances.push(this.reviewFilterDdb);
        wrap2.appendChild(newBtn.element);
        wrap2.appendChild(this.reviewFilterDdb.element);
        header.appendChild(wrap1);
        header.appendChild(wrap2);
        return header;
    }
    getFilterIconCss() {
        return this.reviewFilter === 'all' ? 'e-icons e-filter' : 'e-icons e-filter-active';
    }
    getFilterMenuItems(l10n) {
        const selectedIcon = 'e-icons e-selected-icon';
        const items = [
            { id: 'all', text: l10n.getConstant('All'), iconCss: this.reviewFilter === 'all' ? selectedIcon : '' },
            { id: 'active', text: l10n.getConstant('Active'), iconCss: this.reviewFilter === 'active' ? selectedIcon : '' },
            { id: 'resolved', text: l10n.getConstant('Resolved'), iconCss: this.reviewFilter === 'resolved' ? selectedIcon : '' }
        ];
        return items;
    }
    onFilterMenuSelect(event) {
        const l10n = this.parent.serviceLocator.getService(locale);
        let value = '';
        if (event && event.item) {
            if (event.item.id) {
                value = event.item.id;
            }
            else if (event.item.text) {
                value = event.item.text.toLowerCase();
            }
        }
        const validFilters = ['all', 'active', 'resolved'];
        if (validFilters.indexOf(value) !== -1) {
            this.reviewFilter = value;
        }
        if (this.reviewFilterDdb) {
            this.reviewFilterDdb.setProperties({ items: this.getFilterMenuItems(l10n) }, true);
            this.refreshFilterButton();
        }
        this.renderReviewBody();
    }
    refreshFilterButton() {
        if (this.reviewFilterDdb && this.reviewFilterDdb.element) {
            const iconCss = this.getFilterIconCss();
            this.reviewFilterDdb.iconCss = iconCss;
            this.reviewFilterDdb.dataBind();
        }
    }
    filterMatches(thread) {
        if (this.reviewFilter === 'active') {
            return thread.isResolved !== true;
        }
        else if (this.reviewFilter === 'resolved') {
            return thread.isResolved === true;
        }
        else {
            return true;
        }
    }
    getReviewListHost() {
        let element;
        if (this.reviewBodyEl) {
            if (this.reviewBodyEl.classList.contains('e-review-thread-list')) {
                element = this.reviewBodyEl;
            }
            else {
                element = this.reviewBodyEl.querySelector('.e-review-thread-list');
            }
        }
        return element;
    }
    getReviewListView() {
        const host = this.getReviewListHost();
        return host ? getComponent(host, 'listview') : null;
    }
    removeThreadFromPanel(threadId) {
        const lv = this.getReviewListView();
        if (lv) {
            lv.removeItem({ id: threadId });
            this.ensureEmptyStateIfNoItems();
            this.scheduleMount();
        }
        else {
            this.showEmptyReviewBody();
        }
    }
    insertThreadIntoPanel(thread) {
        if (this.reviewBodyEl && this.filterMatches(thread)) {
            let lv = this.getReviewListView();
            if (!lv) {
                this.renderReviewBody();
                lv = this.getReviewListView();
                if (!lv) {
                    return;
                }
            }
            const sheet = this.parent.getActiveSheet();
            const allThreads = (sheet && sheet.comments) ? sheet.comments : [];
            const filtered = allThreads.filter((t) => this.filterMatches(t));
            const insertIdx = filtered.findIndex((t) => t.id === thread.id);
            if (insertIdx === -1) {
                return;
            }
            const hostExist = this.reviewBodyEl.querySelector(`.e-review-thread-host[data-thread-id="${thread.id}"]`);
            if (!hostExist) {
                lv.addItem([{ id: thread.id }], undefined, insertIdx);
                this.scheduleMount();
            }
        }
    }
    showEmptyReviewBody() {
        this.destroyReviewBodyListView();
        if (this.reviewBodyEl) {
            this.reviewBodyEl.innerHTML = '';
            this.renderEmptyReviewBody();
        }
    }
    ensureEmptyStateIfNoItems() {
        const lv = this.getReviewListView();
        if (lv) {
            const ds = lv.dataSource || [];
            if (!Array.isArray(ds) || ds.length === 0) {
                this.showEmptyReviewBody();
            }
        }
    }
    renderReviewBody() {
        this.renderReviewBodyForSheetIdx(this.parent.activeSheetIndex);
    }
    renderReviewBodyForSheetIdx(sheetIdx) {
        this.ensureReviewBodyHost();
        if (this.reviewBodyEl) {
            this.destroyReviewBodyListView();
            this.reviewBodyEl.innerHTML = '';
            const sheet = this.parent.sheets[sheetIdx];
            const allThreads = (sheet && sheet.comments) ? sheet.comments : [];
            const threads = allThreads.filter((t) => {
                if (this.reviewFilter === 'active') {
                    return t.isResolved !== true;
                }
                else if (this.reviewFilter === 'resolved') {
                    return t.isResolved === true;
                }
                return true;
            });
            if (threads.length === 0) {
                this.renderEmptyReviewBody();
                return;
            }
            this.renderThreadsList(threads);
        }
    }
    ensureReviewBodyHost() {
        const reviewPanel = this.parent.element.querySelector(`#${this.parent.element.id}_review_panel`);
        if (reviewPanel) {
            if (!this.reviewBodyEl) {
                this.reviewBodyEl = this.parent.createElement('div', { className: 'e-review-body' });
                const wrap = reviewPanel.querySelector('.e-review-wrap');
                if (wrap) {
                    wrap.appendChild(this.reviewBodyEl);
                }
            }
        }
    }
    destroyReviewBodyListView() {
        const lv = this.getReviewListView();
        if (lv) {
            this.destoryCommentDdb(lv.element);
            lv.destroy();
        }
    }
    renderEmptyReviewBody() {
        const l10n = this.parent.serviceLocator.getService(locale);
        const empty = this.parent.createElement('div', { className: 'e-review-empty' });
        empty.textContent = l10n.getConstant(this.reviewFilter !== 'all' ? 'EmptyFilterComment' : 'EmptyComment');
        this.reviewBodyEl.appendChild(empty);
    }
    renderThreadsList(threads) {
        const dataSource = threads.map((t) => ({ id: t.id }));
        const itemTemplate = (data) => {
            const item = this.parent.createElement('div', { className: 'e-review-thread', attrs: { id: data.id } });
            const host = this.parent.createElement('div', { className: 'e-review-thread-host', attrs: { 'data-thread-id': data.id } });
            item.appendChild(host);
            return [item];
        };
        enableRipple(false);
        const listView = new ListView({
            dataSource: dataSource,
            template: itemTemplate,
            cssClass: 'e-review-thread-list',
            enableVirtualization: true,
            height: '100%',
            enableRtl: this.parent.enableRtl,
            select: (args) => this.updateCellSelction(args),
            actionComplete: () => this.scheduleMount(),
            scroll: () => this.scheduleMount()
        });
        listView.createElement = this.parent.createElement;
        /* eslint-disable */
        listView.isInternalTemplate = true;
        /* eslint-enable */
        listView.appendTo(this.reviewBodyEl);
        this.reviewInstances.push({ destroy: () => listView.destroy() });
        this.scheduleMount();
    }
    scheduleMount() {
        if (this.scheduleMountId) {
            cancelAnimationFrame(this.scheduleMountId);
        }
        this.scheduleMountId = requestAnimationFrame(() => {
            this.mountVisibleReviewItems();
            this.scheduleMountId = 0;
        });
    }
    mountVisibleReviewItems() {
        if (this.reviewBodyEl) {
            const hosts = this.reviewBodyEl.querySelectorAll('.e-review-thread-host');
            hosts.forEach((host, index) => {
                if (host.getAttribute('data-mounted') !== 'true') {
                    const threadId = host.getAttribute('data-thread-id');
                    if (threadId) {
                        const pos = this.findThreadPositionById(threadId);
                        if (pos) {
                            const container = this.buildPaneThreadContainer(pos.row, pos.col, index);
                            host.appendChild(container);
                            host.setAttribute('data-mounted', 'true');
                        }
                    }
                }
            });
        }
    }
    buildPaneThreadContainer(rIdx, cIdx, index) {
        const container = this.parent.createElement('div', { className: `e-pane-container e-thread-${index}` });
        container.tabIndex = -1;
        this.renderCommentUI(container, rIdx, cIdx, true, `e-thread-${index}`);
        EventHandler.remove(container, 'mouseout', this.mouseOut);
        const cell = getCell(rIdx, cIdx, this.parent.getActiveSheet());
        const thread = cell && cell.comment;
        if (thread && thread.isResolved) {
            this.handleResolvedThread(container, rIdx, cIdx, thread);
        }
        this.bindContainerEvents(container);
        return container;
    }
    findThreadPositionById(threadId) {
        const sheet = this.parent && this.parent.getActiveSheet();
        const coll = sheet && sheet.comments || [];
        const thread = coll.find((t) => t.id === threadId);
        let row;
        let col;
        if (thread && thread.address && Array.isArray(thread.address)) {
            row = thread.address[0];
            col = thread.address[1];
        }
        return { row, col };
    }
    refreshCommentsPane(args) {
        if (this.isReviewPaneVisible) {
            const reviewPanel = this.parent.element.querySelector(`#${this.parent.element.id}_review_panel`);
            if (reviewPanel) {
                this.renderReviewBodyForSheetIdx(args.sheetIdx);
            }
        }
    }
    renderNewCommentForPanel(rIdx, cIdx) {
        let row = rIdx;
        let col = cIdx;
        if (isNullOrUndefined(row) || isNullOrUndefined(col)) {
            const activeAddress = getCellIndexes(this.parent.getActiveSheet().activeCell);
            row = activeAddress[0];
            col = activeAddress[1];
        }
        const sheet = this.parent.getActiveSheet();
        if (!this.reviewBodyEl || (sheet.comments && sheet.comments.length === 0)) {
            this.renderReviewBody();
        }
        const existing = (sheet.comments || []).find((t) => Array.isArray(t.address) && t.address[0] === row && t.address[1] === col);
        if (existing && existing.id) {
            this.updateThreadSelection(existing.id);
            this.scrollToThreadInPanel(existing.id);
            return;
        }
        const emptyEl = this.reviewBodyEl.querySelector('.e-review-empty');
        if (emptyEl) {
            emptyEl.remove();
        }
        const oldDraft = this.reviewBodyEl.querySelector('.e-pane-container.e-thread-draft');
        if (oldDraft) {
            const host = this.getBodyHost(oldDraft);
            if (host) {
                this.unbindReplyHover(host);
            }
            this.unbindContainerEvents(oldDraft);
            oldDraft.remove();
        }
        const draft = this.parent.createElement('div', { className: 'e-pane-container e-thread-draft' });
        draft.tabIndex = -1;
        const targetElement = this.parent.getCell(row, col);
        if (targetElement && !targetElement.querySelector('.e-comment-indicator')) {
            this.createCommentIndicator({ targetEle: targetElement, rIdx: row, cIdx: col });
        }
        this.renderCommentUI(draft, row, col, true);
        const cell = getCell(row, col, this.parent.getActiveSheet());
        const thread = cell && cell.comment;
        if (thread && thread.isResolved) {
            this.handleResolvedThread(draft, row, col, thread);
        }
        this.bindContainerEvents(draft);
        this.reviewBodyEl.insertBefore(draft, this.reviewBodyEl.firstChild);
        const input = draft.querySelector('.e-comment-footer .e-comment-input');
        if (input) {
            input.focus();
            input.select();
            this.adjustTextareaHeight(input, false);
        }
    }
    updateThreadSelection(threadId) {
        if (!this.reviewBodyEl) {
            this.renderReviewBody();
        }
        if (!this.reviewBodyEl.querySelector(`.e-review-thread-host[data-thread-id="${threadId}"]`)) {
            this.renderReviewBody();
        }
        getUpdateUsingRaf(() => {
            const host = this.reviewBodyEl.querySelector(`.e-review-thread-host[data-thread-id="${threadId}"]`);
            if (host) {
                const container = host.querySelector('.e-pane-container');
                if (container) {
                    container.focus();
                    host.scrollIntoView({ block: 'nearest' });
                }
            }
        });
    }
    updateCellSelction(args) {
        if (args && args.item) {
            const host = args.item.querySelector('.e-review-thread-host');
            let container;
            if (host) {
                container = host.querySelector('.e-pane-container');
            }
            else {
                container = closest(args.item, '.e-pane-container');
            }
            if (container) {
                const idx = this.getIndexesFromContainer(container);
                if (idx && idx.length === 2) {
                    this.parent.goTo(getSheetName(this.parent, this.parent.activeSheetIndex) + '!' + getCellAddress(idx[0], idx[1]));
                }
            }
        }
    }
    refreshReviewListFromSheet() {
        if (!this.isReviewPaneVisible || !this.reviewBodyEl) {
            return;
        }
        const sheet = this.parent.getActiveSheet();
        const allThreads = sheet.comments || [];
        const filtered = allThreads.filter((t) => this.filterMatches(t));
        const dataSource = filtered.map((t) => ({ id: t.id }));
        if (dataSource.length === 0) {
            this.showEmptyReviewBody();
            return;
        }
        const listView = this.getReviewListView();
        if (listView) {
            listView.setProperties({ dataSource }, true);
            listView.refresh();
            this.scheduleMount();
        }
        else {
            this.renderReviewBody();
        }
    }
    syncThreadInPanel(thread) {
        if (!this.isReviewPaneVisible || !this.reviewBodyEl || !thread || !thread.id) {
            return;
        }
        if (this.reviewFilter !== 'all') {
            const shouldShow = this.filterMatches(thread);
            const hostExist = this.reviewBodyEl.querySelector(`.e-review-thread-host[data-thread-id="${thread.id}"]`);
            if (shouldShow && !hostExist) {
                this.insertThreadIntoPanel(thread);
            }
            else if (!shouldShow && hostExist) {
                this.removeThreadFromPanel(thread.id);
            }
        }
        const host = this.reviewBodyEl.querySelector(`.e-review-thread-host[data-thread-id="${thread.id}"]`);
        if (host) {
            const container = host.querySelector('.e-pane-container');
            if (!container) {
                this.refreshReviewListFromSheet();
                return;
            }
            const rIdx = thread.address[0];
            const cIdx = thread.address[1];
            if (typeof rIdx === 'number' && typeof cIdx === 'number') {
                this.renderThreadContainer(rIdx, cIdx, thread, container);
            }
            const body = container.querySelector('.e-comment-body');
            if (body) {
                const lv = getComponent(body, 'listview');
                if (lv) {
                    const data = thread ? this.convertThreadToListItems(thread) : [];
                    lv.setProperties({ dataSource: data }, true);
                    lv.refresh();
                }
                else {
                    const newBody = this.createBodyContent(thread, true, true);
                    const footer = container.querySelector('.e-comment-footer');
                    if (newBody && footer) {
                        container.insertBefore(newBody, footer);
                    }
                }
            }
        }
        else {
            this.refreshReviewListFromSheet();
        }
    }
    getFilteredThreadsForActiveSheet() {
        const sheet = this.parent.getActiveSheet();
        const all = sheet.comments || [];
        const result = [];
        for (let i = 0; i < all.length; i++) {
            const t = all[i];
            if (this.filterMatches(t)) {
                result.push(t);
            }
        }
        return result;
    }
    getThreadIndexInFiltered(threadId) {
        const list = this.getFilteredThreadsForActiveSheet();
        for (let i = 0; i < list.length; i++) {
            if (list[i].id === threadId) {
                return i;
            }
        }
        return -1;
    }
    scrollToThreadInPanel(threadId, isSelection) {
        if (!this.isReviewPaneVisible || !this.reviewBodyEl || !threadId) {
            return;
        }
        const lv = this.getReviewListView();
        if (!lv) {
            return;
        }
        const filteredIndex = this.getThreadIndexInFiltered(threadId);
        if (filteredIndex < 0) {
            return;
        }
        lv.selectItem({ id: threadId });
        this.scheduleMount();
        getUpdateUsingRaf(() => {
            this.mountVisibleReviewItems();
            const selector = '.e-review-thread-host[data-thread-id="' + threadId + '"]';
            const host = this.reviewBodyEl.querySelector(selector);
            if (host) {
                host.scrollIntoView({ block: 'nearest' });
                const container = host.querySelector('.e-pane-container');
                if (container) {
                    container.focus();
                    const input = container.querySelector('.e-comment-footer .e-comment-input');
                    if (input && !isSelection) {
                        input.focus();
                        input.select();
                    }
                }
            }
        });
    }
    onCommentUndoRedo(args) {
        if (args && args.cellIdx && !isNullOrUndefined(args.sheetIdx)) {
            const sheet = this.parent.sheets[args.sheetIdx];
            const row = args.cellIdx[0];
            const col = args.cellIdx[1];
            const cell = getCell(row, col, sheet);
            const model = cell && cell.comment;
            const existing = sheet.comments.find((t) => t.address && t.address[0] === row && t.address[1] === col);
            if (model) {
                if (!Array.isArray(model.address)) {
                    model.address = [row, col];
                }
                if (existing) {
                    const idxInColl = sheet.comments.findIndex((c) => c.id === existing.id);
                    if (idxInColl > -1) {
                        if (model.id !== existing.id) {
                            model.id = existing.id;
                        }
                        sheet.comments[idxInColl] = model;
                    }
                }
                else {
                    this.processSheetComments({ sheet: sheet, comment: model, isDelete: false });
                }
                if (this.parent.activeSheetIndex === args.sheetIdx) {
                    const td = this.parent.getCell(row, col);
                    if (td && !td.querySelector('.e-comment-indicator')) {
                        this.createCommentIndicator({ targetEle: td, rIdx: row, cIdx: col });
                    }
                    const cont = this.getCommentContainer();
                    if (cont) {
                        const indices = this.getIndexesFromContainer(cont);
                        if (indices && indices[0] === row && indices[1] === col) {
                            this.renderThreadContainer(row, col, model, cont);
                            const body = cont.querySelector('.e-comment-body');
                            if (body) {
                                const lv = getComponent(body, 'listview');
                                if (lv) {
                                    lv.setProperties({ dataSource: this.convertThreadToListItems(model) }, true);
                                    lv.refresh();
                                }
                            }
                        }
                    }
                }
            }
            else {
                if (existing) {
                    this.processSheetComments({ sheet: sheet, id: existing.id, isDelete: true });
                }
                this.detachCommentIndicator(row, col);
                const cont = this.getCommentContainer();
                if (cont) {
                    const indices = this.getIndexesFromContainer(cont);
                    if (indices && indices[0] === row && indices[1] === col) {
                        this.removeCommentContainer();
                    }
                }
            }
            if (this.isReviewPaneVisible) {
                this.refreshCommentsPane({ sheetIdx: args.sheetIdx });
            }
        }
    }
    navigateNextPrevComment(args) {
        const startSheetIdx = this.parent.activeSheetIndex;
        const activeSheet = this.parent.getActiveSheet();
        const address = getCellIndexes(activeSheet.activeCell);
        const currentColl = activeSheet.comments ? activeSheet.comments : [];
        const navigate = (sheetIdx, address) => {
            this.parent.goTo(`${getSheetName(this.parent, sheetIdx)}!${getRangeAddress(address)}`);
        };
        const crossSheetNavigate = (startIdx) => {
            const sheetCount = this.parent.sheets.length;
            for (let step = 1; step < sheetCount; step++) {
                const index = (startIdx + (args.isNext ? step : -step) + sheetCount) % sheetCount;
                const edge = this.getEdgeThreadFromSheet(this.parent.sheets[index], !args.isNext);
                if (edge && edge.address) {
                    navigate(index, edge.address);
                    return true;
                }
            }
            return false;
        };
        if (currentColl.length > 0) { // Try within the current sheet first
            const pos = getSortedIndex(currentColl, address, true);
            let exactIdx = -1;
            if (pos < currentColl.length) {
                const comment = currentColl[pos];
                if (comment && comment.address && comment.address[0] === address[0] && comment.address[1] === address[1]) {
                    exactIdx = pos;
                }
            }
            if (exactIdx === -1 && pos > 0) {
                const prevComment = currentColl[(pos - 1)];
                if (prevComment && prevComment.address && prevComment.address[0] === address[0] && prevComment.address[1] === address[1]) {
                    exactIdx = pos - 1;
                }
            }
            let idx = args.isNext ? (exactIdx > -1 ? exactIdx + 1 : pos) : (exactIdx > -1 ? exactIdx - 1 : pos - 1);
            if (idx < 0 || idx >= currentColl.length) { // If out of bounds within current sheet, try cross-sheet.
                const navigated = crossSheetNavigate(startSheetIdx);
                if (navigated) {
                    return;
                }
                idx = args.isNext ? 0 : currentColl.length - 1;
            }
            const comment = currentColl[idx];
            if (comment && comment.address) { // Next/Prev within the same sheet
                navigate(startSheetIdx, comment.address);
                return;
            }
        }
        // Cross-sheet fallback: move forward/backward to the next sheet that has comments
        crossSheetNavigate(startSheetIdx);
    }
    getEdgeThreadFromSheet(sheet, fromEnd) {
        const coll = sheet.comments ? sheet.comments : [];
        if (coll.length === 0) {
            return undefined;
        }
        return fromEnd ? coll[coll.length - 1] : coll[0]; // Next => take first comment; Prev => take last comment
    }
    /**
     * Destroy SpreadsheetComment Module.
     *
     * @returns {void} - Destroy SpreadsheetComment module.
     */
    destroy() {
        this.removeEventListener();
        if (this.scheduleMountId) {
            cancelAnimationFrame(this.scheduleMountId);
            this.scheduleMountId = 0;
        }
        if (this.activeReplyDdb) {
            this.activeReplyDdb.destroy();
        }
        this.activeReplyDdb = null;
        if (this.reviewFilterDdb) {
            this.reviewFilterDdb.destroy();
        }
        this.reviewFilterDdb = null;
        if (this.reviewInstances && this.reviewInstances.length) {
            this.reviewInstances.forEach((inst) => { inst.destroy(); });
        }
        this.reviewInstances = [];
        if (this.commentListView) {
            this.commentListView.destroy();
        }
        this.commentListView = null;
        if (this.reviewPaneEl && this.reviewPaneEl.parentElement) {
            this.reviewPaneEl.parentElement.removeChild(this.reviewPaneEl);
        }
        this.reviewPaneEl = null;
        this.reviewFilter = null;
        this.reviewHeaderEl = null;
        this.reviewBodyEl = null;
        this.isReviewPaneVisible = null;
        if (!this.parent.isDestroyed && !this.parent.refreshing) {
            const commentIndicators = this.parent.element.getElementsByClassName('e-comment-indicator');
            while (commentIndicators.length) {
                const indicator = commentIndicators[0];
                if (indicator) {
                    EventHandler.remove(indicator, 'mouseover', this.mouseOver);
                    EventHandler.remove(indicator, 'mouseout', this.mouseOut);
                    delete indicator.dataset.commentListenersAdded;
                    delete indicator.dataset.commentRowIndex;
                    delete indicator.dataset.commentColIndex;
                    detach(indicator);
                }
            }
            this.removeCommentContainer();
        }
        this.isCommentVisible = null;
        this.activeCommentCell = null;
        this.isEditing = null;
        this.editingState = null;
        this.bodyHost = null;
        this.parent = null;
    }
    /**
     * Get the SpreadsheetComment Module Name.
     *
     * @returns {string} - Get the spreadsheetComment module name.
     */
    getModuleName() {
        return 'spreadsheetComment';
    }
}
